import { agreementAllowsCloudStorage, agreementSigned } from '../domain/agreement';
import { createReportRecord, reportFileName, variantsForLanguage, type ReportOutputLanguage, type ReportVariant, type V2ReportRecord } from '../domain/report';
import { clientNameOf, isInspectionLocked, type V2Inspection } from '../domain/inspection';
import { inspectionServiceOf } from '../domain/inspectionService';
import { accountGuard } from '../persistence/accountScope';
import { getReportBytes, putFinalReportAndPurge, putReportWithMedia } from '../persistence/v2Database';
import { renderAgreementDocumentHtml } from '../report/agreementHtml';
import { assembleReport, type PhotoLoader } from '../report/assembly';
import { renderVariantHtml } from '../report/renderHtml';
import type { PdfRenderer } from '../report/pdf';
import { buildCloseout } from './closeout';
import { aiJobsStore } from './aiJobsStore';
import { aiStore } from './aiStore';
import { advancedSummariesOf, liveRowFacts } from './rowFacts';
import { createStore } from './createStore';
import { licenceBlockFor, type LicenceBlock } from './licenceGate';
import { flushReportUploads, queueReportUploads } from './reportCloudStore';
import { requestServerPurge } from './serverPurge';
import { closeInspectionCredit } from './billingStore';
import { currentAgreementPdf } from './agreementPdfStore';
import { contextKindOf } from '../domain/inspectionContext';
import { formalWorkflowRefusalFor, type FormalWorkflowRefusal } from './formalWorkflow';
import { evidenceStore } from './evidenceStore';
import { findingStore } from './findingStore';
import { inspectionStore } from './inspectionStore';
import { prepareWorkingDataPurge } from './purgeInspection';
import { reportReadinessOf } from './reportReadiness';
import { brandingForPrint } from './reportBranding';

// Final report records (Owner task book 2026-09-04 §18). Generation is device-side and offline:
// assembly → HTML → PDF (renderer injected: jsPDF + html2canvas in the app, a fake in tests) →
// ONE transaction per PDF (record + bytes). The last PDF of a run carries the inspection lock AND —
// NY F1 Round 5 (Owner ruling OD-12) — the deletion of the inspection's working data, in the same
// transaction, so "report exists", "locked" and "working data deleted" are never observed apart.
export interface ReportState {
  hydrated: boolean;
  reports: V2ReportRecord[];
}

export const reportStore = createStore<ReportState>({ hydrated: false, reports: [] });

export function resetReportStoreForTests(): void {
  reportStore.set(() => ({ hydrated: false, reports: [] }));
  resetReportPreviewsForTests();
}

export function applyReportSnapshot(reports: V2ReportRecord[]): void {
  reportStore.set(() => ({ hydrated: true, reports: [...reports] }));
}

export function removeReportsOfInspectionLocal(inspectionId: string): void {
  reportStore.set((prev) => ({ ...prev, reports: prev.reports.filter((r) => r.inspectionId !== inspectionId) }));
}

export function reportsOfInspection(state: ReportState, inspectionId: string): V2ReportRecord[] {
  return state.reports.filter((r) => r.inspectionId === inspectionId).sort((a, b) => b.generatedAt - a.generatedAt || a.id.localeCompare(b.id));
}

// ---------- OD-12 preview gate (approved draft §5.1) ----------
//
// "Generate stays disabled until every output has been previewed in this session." The flags live in
// memory only: a preview is an act of reading THIS content, so a restart — or any change to the
// inspection after the preview — asks for it again. What is stored per output is the inspection's
// `updatedAt` at the moment the preview opened, which is exactly what makes a stale preview visible.
interface ReportPreviewState {
  // inspectionId → variant → the inspection's updatedAt when the preview was opened.
  previews: Record<string, Partial<Record<ReportVariant, number>>>;
}

export const reportPreviewStore = createStore<ReportPreviewState>({ previews: {} });

export function resetReportPreviewsForTests(): void {
  reportPreviewStore.set(() => ({ previews: {} }));
}

export function markReportPreviewed(inspectionId: string, variant: ReportVariant): void {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!inspection) return;
  reportPreviewStore.set((prev) => ({
    previews: { ...prev.previews, [inspectionId]: { ...prev.previews[inspectionId], [variant]: inspection.updatedAt } }
  }));
}

export function clearReportPreviews(inspectionId: string): void {
  reportPreviewStore.set((prev) => {
    if (!prev.previews[inspectionId]) return prev;
    const next = { ...prev.previews };
    delete next[inspectionId];
    return { previews: next };
  });
}

export interface ReportOutputStatus {
  variant: ReportVariant;
  previewed: boolean;
  // Owner 2026-10-21: the signed agreement needs no preview — both parties read it when signing, and its
  // PDF is made right then (src/state/agreementPdfStore.ts). Every report output still does (OD-12).
  needsPreview: boolean;
}

export interface ReportPreviewStatus {
  outputs: ReportOutputStatus[];
  pending: ReportVariant[];
  allPreviewed: boolean;
}

// What the Report screen shows next to each output, and what generation refuses on.
export function reportPreviewStatusOf(inspectionId: string, language: ReportOutputLanguage, previews: ReportPreviewState = reportPreviewStore.get()): ReportPreviewStatus {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
  const variants = inspection ? outputVariantsFor(language, agreementSigned(inspection.agreement), inspection.clientChineseSummary) : variantsForLanguage(language);
  const seen = previews.previews[inspectionId] ?? {};
  const outputs = variants.map((variant) => ({ variant, previewed: inspection !== null && seen[variant] === inspection.updatedAt, needsPreview: variant !== 'agreement' }));
  const pending = outputs.filter((o) => o.needsPreview && !o.previewed).map((o) => o.variant);
  return { outputs, pending, allPreviewed: pending.length === 0 };
}

export type GenerateOutcome =
  | { ok: true; records: V2ReportRecord[] }
  // 'notReady'      = NY F1 Round 2 REPORT_READINESS_BLOCK (OC-2): detail = unresolved descriptors.
  // 'notPreviewed'  = NY F1 Round 5 OD-12 preview gate: detail = outputs still to preview.
  | { ok: false; reason: 'notFound' | 'locked' | 'blocked' | 'notReady' | 'notPreviewed' | 'renderFailed' | LicenceBlock | FormalWorkflowRefusal; detail: string; records: V2ReportRecord[] };

export interface GenerateDeps {
  renderer: PdfRenderer;
  loadPhoto?: PhotoLoader;
  now?: number;
}

// The final output set (Round 2 §AK): the report variant(s) for the chosen language, PLUS — when the
// agreement is signed — the signed Inspection Agreement as its own PDF. Never one merged file. The
// client's Chinese summary (Owner item 23), when switched on, is one more PDF of its own.
export function outputVariantsFor(language: ReportOutputLanguage, agreementIsSigned: boolean, clientChineseSummary: boolean = false): ReportVariant[] {
  const reports: ReportVariant[] = [...variantsForLanguage(language), ...(clientChineseSummary ? (['clientChineseSummary'] as const) : [])];
  return agreementIsSigned ? [...reports, 'agreement'] : reports;
}

// English → one English client PDF. Chinese → the English client PDF + the Chinese inspector copy.
// A signed agreement adds its own separate PDF. Refuses while closeout still has blocking gaps
// (nothing is exported silently incomplete), while an output has not been previewed (OD-12), and
// once the inspection is already finalized.
export async function generateFinalReportAction(inspectionId: string, language: ReportOutputLanguage, deps: GenerateDeps): Promise<GenerateOutcome> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!inspection) return { ok: false, reason: 'notFound', detail: '', records: [] };
  // A copy exported to another device (Owner 2026-10-20) is never finalized here: the work goes on there.
  if (isInspectionLocked(inspection) || inspection.movedOut) return { ok: false, reason: 'locked', detail: '', records: [] };
  // NY F1 Round 1 (§15.1 effect 2 "no ordinary NY client Home Inspection Report"; §7.3 "report
  // generation is gated on the confirmation" / "report generation blocked"). ELIGIBILITY is checked
  // BEFORE readiness: whether the supported New York workflow applies to this inspection at all is a
  // different — and prior — question to whether its content is complete. Round 5 adds X-1 to it: an
  // unsigned engagement has no report either.
  //
  // This is NOT OC-2's Report Readiness block over unresolved mandatory descriptors, which is Round 2
  // and is checked next. It also does not replace the continuation seam: OC-1 forbids checking the
  // client gates ONLY at report generation, it does not exempt the report.
  const refusal = formalWorkflowRefusalFor(inspection);
  if (refusal) return { ok: false, reason: refusal, detail: '', records: [] };
  // 2026-10-19 (Owner items 11 / 50): a formal report needs the account's bound, current licence.
  const licence = licenceBlockFor(inspection, deps.now ?? Date.now());
  if (licence) return { ok: false, reason: licence, detail: '', records: [] };
  const closeout = buildCloseout(inspectionId, inspectionStore.get(), findingStore.get(), liveRowFacts());
  if (!closeout.canFinalize) return { ok: false, reason: 'blocked', detail: String(closeout.blocking.length), records: [] };
  // NY F1 Round 2 (Owner correction OC-2): REPORT READINESS is the second, independent gate. An
  // otherwise eligible, closeout-clean inspection is still refused — before any PDF, any record and
  // the lock — while a mandatory descriptor is unresolved. Decided by the one canonical evaluator
  // (src/state/reportReadiness.ts); nothing is restated here.
  const readiness = reportReadinessOf(inspectionId);
  if (!readiness.ready) return { ok: false, reason: 'notReady', detail: String(readiness.blockers.length), records: [] };
  // NY F1 Round 5 (OD-12): the THIRD gate — every report output was previewed, in this session, as it
  // stands now. The inspector sees exactly what the client will get before it becomes unchangeable.
  // The signed agreement is not among them (Owner 2026-10-21): it was read and signed as it is.
  const previews = reportPreviewStatusOf(inspectionId, language);
  if (!previews.allPreviewed) return { ok: false, reason: 'notPreviewed', detail: String(previews.pending.length), records: [] };

  const now = deps.now ?? Date.now();
  const service = inspectionServiceOf(inspection.property);
  const variants = outputVariantsFor(language, agreementSigned(inspection.agreement), inspection.clientChineseSummary);
  const records: V2ReportRecord[] = [];
  // ACCOUNT ISOLATION: rendering a long report takes a while. Should the account be left meanwhile —
  // and another one entered — this inspection and its PDFs are not written into that account's
  // database: the generation stops and the inspection stays, unfinalized, where it belongs.
  const sameAccount = accountGuard();
  // 2026-10-19 (Owner item 18): the inspector's branding, read once for every output of this run.
  const branding = await brandingForPrint();
  for (const [index, variant] of variants.entries()) {
    const last = index === variants.length - 1;
    try {
      // Owner 2026-10-21: the agreement PDF made when both parties signed is used as it is, unless the
      // agreement has changed since (then it is rendered here, from the agreement as it stands).
      const kept = variant === 'agreement' ? await currentAgreementPdf(inspection) : null;
      const pdf = kept
        ? { bytes: kept.bytes, pageCount: kept.pageCount }
        : await deps.renderer.render(
            variant === 'agreement'
              ? renderAgreementDocumentHtml(inspection, now)
              : renderVariantHtml(
                  variant,
                  await assembleReport(inspectionId, variant, {
                    inspection: inspectionStore.get(),
                    findings: findingStore.get(),
                    evidence: evidenceStore.get(),
                    ai: aiStore.get(),
                    advancedSummaries: advancedSummariesOf(aiJobsStore.get().jobs),
                    loadPhoto: deps.loadPhoto,
                    generatedAt: now,
                    branding,
                    severityLabels: branding.severityLabels
                  })
                )
          );
      if (!sameAccount()) return { ok: false, reason: 'renderFailed', detail: 'the account was left while the report was being generated', records };
      const record = createReportRecord(
        {
          inspectionId,
          language,
          variant,
          fileName: reportFileName(inspection.property.address, now, variant, inspection.agreement.demo, service),
          byteSize: pdf.bytes.byteLength,
          pageCount: pdf.pageCount
        },
        now + index
      );
      if (last) {
        // ONE transaction: the last PDF, its bytes, the finalized + purged inspection record and the
        // deletion of every working record of this inspection (OD-12). Everything the report needed
        // was already rendered above, from the live state.
        const purge = prepareWorkingDataPurge(inspection, { pdfCount: variants.length, generatedAt: now, now });
        await putFinalReportAndPurge(record, { type: 'application/pdf', bytes: pdf.bytes }, purge.write ? [purge.write] : [], purge.keys);
        reportStore.set((prev) => ({ ...prev, reports: [...prev.reports, record] }));
        purge.apply();
      } else {
        await putReportWithMedia(record, { type: 'application/pdf', bytes: pdf.bytes });
        reportStore.set((prev) => ({ ...prev, reports: [...prev.reports, record] }));
      }
      records.push(record);
    } catch (raw) {
      return { ok: false, reason: 'renderFailed', detail: raw instanceof Error ? raw.message : String(raw), records };
    }
  }
  clearReportPreviews(inspectionId);
  // 2026-10-19 (Owner items 1 / 2 / 25): the delivered PDFs are copied to the account (queued, retried) — only
  // when the client consented to that storage in the agreement (v4 consent clause (2), 2026-10-20) — and the
  // server's process data of this inspection — its AI review records — is deleted (queued, retried).
  if (agreementAllowsCloudStorage(inspection.agreement)) queueReportUploads(records, [inspection.property.address, inspection.property.city].filter((part) => part.trim().length > 0).join(', '));
  void flushReportUploads();
  void requestServerPurge(inspectionId);
  // Owner 2026-10-20 (billingPolicy.ts V-2): no longer open on any device. A Demo / test inspection was never
  // recorded by the server.
  if (contextKindOf(inspection) === 'realClient') void closeInspectionCredit(inspectionId).catch(() => undefined);
  return { ok: true, records };
}

export function loadReportFileAction(reportId: string): Promise<Blob | null> {
  return getReportBytes(reportId);
}

// ---------- the delivered row (approved draft §5.3) ----------

// What the Reports list shows for one inspection: 地址 · 日期 · 客户 · PDF 数 · 已出报告. Derived from
// the record that survives a purge, so it reads the same before and after the working data is gone.
export interface ReportSummaryRow {
  inspectionId: string;
  address: string;
  inspectionAt: number;
  clientName: string;
  pdfCount: number;
  generatedAt: number | null;
  purged: boolean;
  demo: boolean;
}

export function reportSummaryOf(inspection: V2Inspection, state: ReportState = reportStore.get()): ReportSummaryRow {
  const files = reportsOfInspection(state, inspection.id);
  const p = inspection.property;
  return {
    inspectionId: inspection.id,
    address: [p.address, p.city, [p.state, p.zip].filter((s) => s.trim().length > 0).join(' ')].map((s) => s.trim()).filter((s) => s.length > 0).join(', '),
    inspectionAt: inspection.inspectionAt,
    clientName: clientNameOf(inspection),
    pdfCount: files.length > 0 ? files.length : (inspection.purge?.pdfCount ?? 0),
    generatedAt: inspection.finalizedAt,
    purged: inspection.purge !== null,
    demo: inspection.agreement.demo
  };
}
